import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import type { PipelineConfigResponse } from '../../types/ai-pipeline';

export interface ModelConfigPanelProps {
  readonly config: PipelineConfigResponse | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
}

/**
 * Read-only display of AI pipeline model configuration.
 * Shows decomposition model ID, embedding model ID, and pipeline version
 * using a semantic definition list for accessibility.
 */
export function ModelConfigPanel({ config, isLoading, error, onRetry }: ModelConfigPanelProps): React.ReactElement {
  if (error) {
    return (
      <Alert variant="destructive" aria-live="assertive">
        <AlertTitle>Failed to load model configuration</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <span>{error}</span>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading && !config) {
    return (
      <div className="flex items-center gap-2 p-4" role="status" aria-live="polite">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
        <span className="text-sm text-muted-foreground">Loading model configuration…</span>
      </div>
    );
  }

  if (!config) {
    return (
      <p className="text-sm text-muted-foreground p-4">No configuration data available.</p>
    );
  }

  return (
    <dl className="space-y-4" aria-live="polite" aria-label="Model configuration">
      <div>
        <dt className="text-sm font-medium text-muted-foreground">Decomposition Model</dt>
        <dd className="mt-1 font-mono text-sm">{config.decompositionModelId}</dd>
      </div>
      <div>
        <dt className="text-sm font-medium text-muted-foreground">Embedding Model</dt>
        <dd className="mt-1 font-mono text-sm">{config.embeddingModelId}</dd>
      </div>
      <div>
        <dt className="text-sm font-medium text-muted-foreground">Pipeline Version</dt>
        <dd className="mt-1 text-sm">{config.pipelineVersion}</dd>
      </div>
    </dl>
  );
}
